import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import { contractsApi } from '../../../utils/workApi';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import { Button, Table, Space, message, Dropdown, Upload } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  FileProtectOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  MoreOutlined,
  LinkOutlined,
  DownloadOutlined,
  CheckOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';
import {
  buildWorkListFilters,
  getDefaultWorkDateRange,
  createWorkDateRangeFilter,
  applyWorkDateRangeChange,
} from '../../../utils/workFilterUtils';
import { buildCommonDatePresets } from '../../../utils/filterDateUtils';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';
import { toJalali } from '../../../utils/dateUtils';
import {
  getContractPublicUrl,
  hasContractSignature,
} from '../../../utils/contractSignatureUtils';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const formatContractDate = (row, originalKey, valueKey) => {
  const raw = row?.[originalKey] || row?.[valueKey];
  if (!raw) return '—';
  return toJalali(raw) || String(raw).slice(0, 10);
};

const Contracts = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDelete, canDeleteRecord } = useResourcePermissions('contracts');
  const defaultRange = useMemo(() => getDefaultWorkDateRange(), []);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [signingId, setSigningId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    contract_types: [],
    projects: [],
  });
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    client_id: 'all',
    contract_type: 'all',
    project_id: 'all',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const signTargetRef = useRef(null);
  const signUploadBtnRef = useRef(null);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, searchText, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const apiFilters = buildWorkListFilters(
        { ...filters, page: pagination.current, per_page: pagination.pageSize },
        searchText,
        defaultRange,
      );
      const res = await contractsApi.getAll(apiFilters);
      const block = res?.data?.contracts || res?.contracts;
      setRows(block?.data || []);
      setTotal(block?.total ?? block?.data?.length ?? 0);
      if (res?.filters) {
        setFilterOptions({
          clients: res.filters.clients || [],
          contract_types: res.filters.contract_types || [],
          projects: res.filters.projects || [],
        });
      }
    } catch {
      message.error('خطا در دریافت قراردادها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) return;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      fetchRows();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchText]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('حداقل یک قرارداد انتخاب کنید');
      return;
    }
    confirmWorkDelete({
      title: 'حذف قراردادها',
      content: `آیا از حذف ${selectedRowKeys.length} قرارداد انتخاب‌شده اطمینان دارید؟`,
      onOk: async () => {
        setBulkLoading(true);
        try {
          await contractsApi.applyQuickAction('delete', selectedRowKeys);
          message.success('قراردادها حذف شدند');
          setSelectedRowKeys([]);
          fetchRows();
        } catch {
          message.error('خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleRowDelete = (rowId) => {
    confirmWorkDelete({
      title: 'حذف قرارداد',
      content: 'آیا از حذف این قرارداد اطمینان دارید؟',
      onOk: async () => {
        try {
          await contractsApi.applyQuickAction('delete', [rowId]);
          message.success('قرارداد حذف شد');
          fetchRows();
        } catch {
          message.error('خطا در حذف');
        }
      },
    });
  };

  const copyPublicLink = async (row) => {
    const url = getContractPublicUrl(row.hash);
    if (!url) {
      message.warning('لینک عمومی برای این قرارداد موجود نیست');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      message.success('لینک عمومی کپی شد');
    } catch {
      message.info(url);
    }
  };

  const handleDownloadContract = async (row) => {
    try {
      await contractsApi.download(row.id, `contract-${row.contract_number || row.id}.pdf`);
      message.success('دانلود آغاز شد');
    } catch {
      message.error('خطا در دانلود قرارداد');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await contractsApi.exportList({
        startDate: filters.startDate,
        endDate: filters.endDate,
        client_id: filters.client_id ?? 'all',
        signed: 'all',
        contract_type: filters.contract_type ?? 'all',
        project_id: filters.project_id ?? 'all',
        search: searchText || undefined,
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const handleSignUpload = async (file) => {
    const contractId = signTargetRef.current;
    if (!contractId || !file) return false;
    setSigningId(contractId);
    try {
      await contractsApi.sign(contractId, file);
      message.success('امضای شرکت ثبت شد');
      fetchRows();
    } catch {
      message.error('خطا در ثبت امضا');
    } finally {
      setSigningId(null);
      signTargetRef.current = null;
    }
    return false;
  };

  const triggerSign = (rowId) => {
    signTargetRef.current = rowId;
    signUploadBtnRef.current?.click();
  };

  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: label('حذف انتخاب‌شده‌ها'),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  const getRowActionItems = (row) => {
    const items = [
      {
        key: 'view',
        label: label('مشاهده'),
        icon: <EyeOutlined />,
        onClick: () =>
          navigateToDetail(`/work/contracts/${row.id}`, {}, {
            listPath: '/work/contracts',
            listLabel: 'قراردادها',
            entityLabel: row.subject || row.contract_number || `قرارداد #${row.id}`,
          }),
      },
    ];

    if (!canEditRecord || canEditRecord(row)) {
      items.push({
        key: 'sign',
        label: label(hasContractSignature(row.company_signature) ? 'امضا ثبت شده' : 'امضای شرکت'),
        icon: <CheckOutlined />,
        disabled: hasContractSignature(row.company_signature) || signingId === row.id,
        onClick: () => triggerSign(row.id),
      });
    }

    items.push({
      key: 'public-link',
      label: label('لینک عمومی'),
      icon: <LinkOutlined />,
      onClick: () => copyPublicLink(row),
    });

    if (canAdd) {
      items.push({
        key: 'copy',
        label: label('کپی قرارداد'),
        icon: <CopyOutlined />,
        onClick: () =>
          navigate('/work/contracts/add', {
            state: {
              copyFromId: row.id,
              returnTo: '/work/contracts',
            },
          }),
      });
    }

    if (!canEditRecord || canEditRecord(row)) {
      items.push({
        key: 'edit',
        label: label('ویرایش'),
        icon: <EditOutlined />,
        onClick: () => navigateToDetail(`/work/contracts/${row.id}/edit`),
      });
    }

    if (canDelete && (!canDeleteRecord || canDeleteRecord(row))) {
      items.push({
        key: 'delete',
        label: label('حذف'),
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleRowDelete(row.id),
      });
    }

    items.push({
      key: 'download',
      label: label('دانلود'),
      icon: <DownloadOutlined />,
      onClick: () => handleDownloadContract(row),
    });

    return items;
  };

  const quickFilters = [
    {
      key: 'contract_type',
      type: 'select',
      label: 'نوع قرارداد',
      value: filters.contract_type === 'all' ? undefined : filters.contract_type,
      options: (filterOptions.contract_types || []).map((t) => ({
        value: t.id,
        label: t.name,
      })),
    },
    {
      key: 'client_id',
      type: 'select',
      label: 'مشتری',
      value: filters.client_id === 'all' ? undefined : filters.client_id,
      options: (filterOptions.clients || []).map((c) => ({ value: c.id, label: c.name })),
      showSearch: true,
    },
    {
      key: 'project_id',
      type: 'select',
      label: 'پروژه',
      value: filters.project_id === 'all' ? undefined : filters.project_id,
      options: (filterOptions.projects || []).map((p) => ({
        value: p.id,
        label: p.project_name,
      })),
      showSearch: true,
    },
    createWorkDateRangeFilter(filters.startDate, filters.endDate),
    {
      key: 'datePreset',
      type: 'datePreset',
      label: 'بازه سریع',
      placeholder: 'بازه سریع',
      options: buildCommonDatePresets(),
      onPreset: (range) => {
        setFilters((prev) => ({
          ...prev,
          startDate: range.startDate,
          endDate: range.endDate,
        }));
        setPagination((p) => ({ ...p, current: 1 }));
      },
    },
  ];

  const columns = [
    {
      title: label('شماره'),
      dataIndex: 'contract_number',
      width: 100,
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v}</span>,
    },
    {
      title: label('موضوع'),
      dataIndex: 'subject',
      ellipsis: true,
      render: (t, row) => (
        <button
          type="button"
          className="text-indigo-600 font-medium bg-transparent border-0 p-0 cursor-pointer"
          style={{ fontFamily: 'CustomFont' }}
          onClick={() =>
            navigateToDetail(`/work/contracts/${row.id}`, {}, {
              listPath: '/work/contracts',
              listLabel: 'قراردادها',
              entityLabel: row.subject || row.contract_number || `قرارداد #${row.id}`,
            })
          }
        >
          {t}
        </button>
      ),
    },
    {
      title: label('مشتری'),
      render: (_, row) => <span style={{ fontFamily: 'CustomFont' }}>{row.client?.name || '—'}</span>,
    },
    {
      title: label('پروژه'),
      render: (_, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>{row.project?.project_name || '—'}</span>
      ),
    },
    {
      title: label('نوع'),
      render: (_, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>{row.contract_type?.name || '—'}</span>
      ),
    },
    {
      title: label('مبلغ'),
      dataIndex: 'amount',
      render: (a, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatWorkPriceDisplay(a, { suffix: row.currency?.currency_symbol })}
        </span>
      ),
    },
    {
      title: label('تاریخ شروع'),
      width: 120,
      render: (_, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatContractDate(row, 'original_start_date', 'start_date')}
        </span>
      ),
    },
    {
      title: label('تاریخ پایان'),
      width: 120,
      render: (_, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatContractDate(row, 'original_end_date', 'end_date')}
        </span>
      ),
    },
    workActionsColumn((_, row) => (
      <Dropdown menu={{ items: getRowActionItems(row) }} trigger={['click']}>
        <Button
          type="text"
          icon={<MoreOutlined />}
          loading={signingId === row.id}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    )),
  ];

  const rowSelection =
    canDelete
      ? {
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }
      : undefined;

  return (
    <WorkListPage
      title="قراردادها"
      subtitle="مدیریت قراردادهای مشتری"
      icon={FileProtectOutlined}
      headerExtra={(
        <Space wrap>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            loading={exporting}
            style={{ fontFamily: 'CustomFont' }}
          >
            خروجی اکسل
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={() => navigate('/work/contracts/templates')}
            style={{ fontFamily: 'CustomFont' }}
          >
            قالب قرارداد
          </Button>
          <PermissionGate permission="add_contract">
            {canAdd ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                onClick={() => navigate('/work/contracts/add')}
                style={{ fontFamily: 'CustomFont' }}
              >
                قرارداد جدید
              </Button>
            ) : null}
          </PermissionGate>
        </Space>
      )}
    >
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={handleSignUpload}
      >
        <button ref={signUploadBtnRef} type="button" style={{ display: 'none' }} aria-hidden />
      </Upload>

            <StandardFilterPanel
              searchValue={searchText}
              onSearchChange={setSearchText}
              onSearch={() => {}}
              searchPlaceholder="جستجو..."
              quickFilters={quickFilters}
              advancedFilters={[]}
              onFilterChange={(k, v) => {
                if (k === 'date_range') {
                  const next = applyWorkDateRangeChange(v, defaultRange);
                  if (!next) return;
                  setFilters((p) => ({ ...p, ...next }));
                  return;
                }
                setFilters((p) => ({ ...p, [k]: v ?? 'all' }));
              }}
              onResetFilters={() => {
                setFilters({
                  startDate: defaultRange.startDate,
                  endDate: defaultRange.endDate,
                  client_id: 'all',
                  contract_type: 'all',
                  project_id: 'all',
                });
                setSearchText('');
              }}
              totalCount={total}
              showAdvancedFilters={false}
              showTotalCount
            />

            {selectedRowKeys.length > 0 && canDelete ? (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkLoading}>
                  <Button
                    type="primary"
                    loading={bulkLoading}
                    icon={<DownOutlined />}
                    className={PANEL_PRIMARY_BTN_CLASS}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
                <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ fontFamily: 'CustomFont' }}>
                  لغو انتخاب
                </Button>
              </div>
            ) : null}

            {loading ? (
              <TableSkeleton rows={6} columns={5} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                rowSelection={rowSelection}
                scroll={{ x: 1100 }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
                }}
              />
            )}
    </WorkListPage>
  );
};

export default Contracts;
